import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Alert,
    CircularProgress,
    Tabs,
    Tab,
    InputAdornment,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Chip,
    Divider,
    Tooltip,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import VerifiedIcon from '@mui/icons-material/Verified';
import PendingIcon from '@mui/icons-material/Pending';
import MergeIcon from '@mui/icons-material/MergeType';
import WarningIcon from '@mui/icons-material/Warning';
import { API_BASE_URL } from '../config';

/**
 * AccountLinking - Full credential management and merge component
 * 
 * Implements:
 * - ACL-2/3: Link/merge initiation
 * - ACL-4: PIN verification
 * - ACL-5: Account merging
 * - ACL-7: Credential management
 */
const AccountLinking = ({ open, onClose, user, onAccountLinked }) => {
    const { t } = useTranslation();
    
    // State management
    const [step, setStep] = useState('list'); // 'list', 'add', 'verify', 'merge_confirm'
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Credentials list
    const [credentials, setCredentials] = useState([]);
    const [primaryUser, setPrimaryUser] = useState(null);
    
    // Add credential
    const [credentialType, setCredentialType] = useState('email');
    const [credentialValue, setCredentialValue] = useState('');
    
    // Verification
    const [verificationId, setVerificationId] = useState(null);
    const [verificationCode, setVerificationCode] = useState('');
    const [expiresIn, setExpiresIn] = useState(0);
    const [action, setAction] = useState('link'); // 'link' or 'merge'
    
    // Merge info
    const [mergeTarget, setMergeTarget] = useState(null);

    // Add email via confirmation link
    const [newEmail, setNewEmail] = useState('');
    const [emailSent, setEmailSent] = useState(false);

    // Create username/password (for WhatsApp users)
    const [newUsername, setNewUsername] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);

    // Determine if current user is WhatsApp-only (no username/password)
    const isWhatsAppOnly = user && (
        user.login_method === 'whatsapp' ||
        (user.username && user.username.includes('@whatsapp.user'))
    );

    // Load credentials on open
    useEffect(() => {
        if (open) {
            loadCredentials();
        }
    }, [open]);

    // Countdown timer for verification
    useEffect(() => {
        if (expiresIn > 0) {
            const timer = setTimeout(() => setExpiresIn(expiresIn - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [expiresIn]);

    const loadCredentials = useCallback(async () => {
        setLoading(true);
        setError('');
        
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/get_credentials.php`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            const data = await response.json();
            
            if (response.ok) {
                setCredentials(data.credentials || []);
                setPrimaryUser(data.primary_user);
            } else {
                setError(data.message || 'Failed to load credentials');
            }
        } catch (err) {
            setError('Failed to connect to server');
        } finally {
            setLoading(false);
        }
    }, []);

    const handleClose = () => {
        setStep('list');
        setError('');
        setSuccess('');
        setCredentialValue('');
        setVerificationCode('');
        setVerificationId(null);
        setMergeTarget(null);
        setNewEmail('');
        setEmailSent(false);
        setNewUsername('');
        setNewPassword('');
        onClose();
    };

    const handleSendEmailConfirmation = async () => {
        const emailTrimmed = newEmail.trim().toLowerCase();
        if (!emailTrimmed) {
            setError('Please enter an email address');
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
            setError('Please enter a valid email address');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/initiate_email_confirmation.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ email: emailTrimmed })
            });
            const data = await response.json();
            if (response.ok && data.success) {
                setEmailSent(true);
            } else {
                setError(data.message || 'Failed to send confirmation email');
            }
        } catch (err) {
            setError('Failed to connect to server');
        } finally {
            setLoading(false);
        }
    };

    const handleInitiateLink = async () => {
        if (!credentialValue) {
            setError(t('fill_all_fields'));
            return;
        }

        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/initiate_link_merge.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    credential_type: credentialType,
                    credential_value: credentialValue
                })
            });

            const data = await response.json();

            if (response.ok) {
                if (data.action === 'merge_required') {
                    // Show merge confirmation
                    setMergeTarget(data.other_account);
                    setStep('merge_confirm');
                } else if (data.action === 'link') {
                    // Proceed to send PIN
                    await sendVerificationPIN('link', null);
                } else if (data.action === 'already_linked') {
                    setError(data.message);
                }
            } else {
                setError(data.message || 'Failed to initiate linking');
            }
        } catch (err) {
            setError('Failed to connect to server');
        } finally {
            setLoading(false);
        }
    };

    const sendVerificationPIN = async (actionType, targetPuId) => {
        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/send_merge_pin.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    credential_type: credentialType,
                    credential_value: credentialValue,
                    action: actionType,
                    target_pu_id: targetPuId
                })
            });

            const data = await response.json();

            if (response.ok) {
                setVerificationId(data.verification_id);
                setExpiresIn(data.expires_in || 300);
                setAction(actionType);
                setStep('verify');
                // Don't set success here - the verification step already shows the message
                setSuccess('');
            } else {
                setError(data.message || 'Failed to send verification code');
            }
        } catch (err) {
            setError('Failed to connect to server');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyPIN = async () => {
        if (!verificationCode || verificationCode.length !== 6) {
            setError(t('enter_verification_code'));
            return;
        }

        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/verify_merge_pin.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    verification_id: verificationId,
                    pin: verificationCode
                })
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess(data.action === 'merged' 
                    ? t('accounts_merged_success') 
                    : t('account_linked_success')
                );
                
                // Reload credentials
                await loadCredentials();
                
                // Notify parent
                if (onAccountLinked) {
                    onAccountLinked();
                }
                
                // Go back to list after success
                setTimeout(() => {
                    setStep('list');
                    setSuccess('');
                    setCredentialValue('');
                    setVerificationCode('');
                }, 2000);
            } else {
                setError(data.message || t('invalid_verification_code'));
            }
        } catch (err) {
            setError('Failed to connect to server');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteCredential = async (credentialId) => {
        if (!window.confirm(t('confirm_delete_credential'))) {
            return;
        }

        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/delete_credential.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    credential_id: credentialId
                })
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess(t('credential_deleted'));
                await loadCredentials();
            } else {
                setError(data.message || 'Failed to delete credential');
            }
        } catch (err) {
            setError('Failed to connect to server');
        } finally {
            setLoading(false);
        }
    };

    const handleSetUsernamePassword = async () => {
        if (!newUsername || !newPassword) {
            setError(t('fill_all_fields'));
            return;
        }
        if (newPassword.length < 6) {
            setError(t('password_helper'));
            return;
        }
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/set_username_password.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ username: newUsername, password: newPassword })
            });
            const data = await response.json();
            if (response.ok) {
                setSuccess(t('username_password_set'));
                if (onAccountLinked) onAccountLinked({ ...user, email: newUsername, login_method: 'email' });
                setTimeout(() => { setStep('list'); setSuccess(''); setNewUsername(''); setNewPassword(''); }, 2000);
            } else {
                setError(data.message || t('server_error'));
            }
        } catch (err) {
            setError('Failed to connect to server');
        } finally {
            setLoading(false);
        }
    };

    const getCredentialIcon = (type) => {
        switch (type) {
            case 'whatsapp':
                return <WhatsAppIcon sx={{ color: '#25d366' }} />;
            case 'email':
                return <EmailIcon sx={{ color: '#1976d2' }} />;
            default:
                return <EmailIcon />;
        }
    };

    const getStatusChip = (status) => {
        switch (status) {
            case 'Active':
                return <Chip icon={<VerifiedIcon />} label={t('verified')} color="success" size="small" />;
            case 'PendingVerification':
                return <Chip icon={<PendingIcon />} label={t('pending')} color="warning" size="small" />;
            case 'Revoked':
                return <Chip label={t('revoked')} color="error" size="small" />;
            default:
                return null;
        }
    };

    // Render credentials list
    const renderCredentialsList = () => {
        const emailCredentials = credentials.filter(c => c.credential_type === 'email' && c.status !== 'Revoked' && (c.credential_value || '').includes('@'));
        return (
            <Box>
                {primaryUser && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                            {t('account_name')}
                        </Typography>
                        <Typography variant="h6">
                            {primaryUser.display_name}
                        </Typography>
                    </Box>
                )}

                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                    Linked Email Addresses
                </Typography>

                {emailCredentials.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No email addresses linked yet.
                    </Typography>
                ) : (
                    <List disablePadding sx={{ mb: 2 }}>
                        {emailCredentials.map((cred) => (
                            <ListItem key={cred.credential_id} divider sx={{ px: 0 }}>
                                <ListItemIcon>
                                    <EmailIcon sx={{ color: '#1976d2' }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={cred.credential_value}
                                    secondary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                            {getStatusChip(cred.status)}
                                            {cred.is_primary && (
                                                <Chip label={t('primary')} size="small" variant="outlined" />
                                            )}
                                        </Box>
                                    }
                                />
                                <ListItemSecondaryAction>
                                    {cred.can_delete && (
                                        <IconButton
                                            edge="end"
                                            onClick={() => handleDeleteCredential(cred.credential_id)}
                                            disabled={loading}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                )}

                <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => { setNewEmail(''); setEmailSent(false); setError(''); setStep('add_email'); }}
                    color="primary"
                >
                    Add Email
                </Button>

                {isWhatsAppOnly && (
                    <Button
                        fullWidth
                        variant="outlined"
                        color="primary"
                        startIcon={<EmailIcon />}
                        onClick={() => setStep('set_credentials')}
                        sx={{ mt: 1.5 }}
                    >
                        {t('create_username_password')}
                    </Button>
                )}
            </Box>
        );
    };

    // Render add email via confirmation link
    const renderAddEmailForm = () => (
        <Box>
            {emailSent ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                    Confirmation email sent to <strong>{newEmail}</strong>. Check your inbox and click the link to confirm — the link expires in 24 hours.
                </Alert>
            ) : (
                <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Enter an email address to link to your account. We&apos;ll send a confirmation link — click it to complete the process.
                    </Typography>

                    <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder="you@example.com"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <EmailIcon />
                                </InputAdornment>
                            ),
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSendEmailConfirmation(); }}
                        sx={{ mb: 2 }}
                    />

                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleSendEmailConfirmation}
                        disabled={loading || !newEmail.trim()}
                        startIcon={loading ? null : <AddIcon />}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Add Email'}
                    </Button>
                </>
            )}

            <Button
                fullWidth
                variant="text"
                onClick={() => { setStep('list'); setNewEmail(''); setEmailSent(false); setError(''); }}
                sx={{ mt: 1 }}
            >
                {t('cancel_btn')}
            </Button>
        </Box>
    );

    // Render add credential form
    const renderAddCredential = () => (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('add_credential_desc')}
            </Typography>

            <Tabs
                value={credentialType === 'email' ? 0 : 1}
                onChange={(e, val) => setCredentialType(val === 0 ? 'email' : 'whatsapp')}
                variant="fullWidth"
                sx={{ mb: 2 }}
            >
                <Tab icon={<EmailIcon />} label={t('email')} />
                <Tab icon={<WhatsAppIcon />} label={t('whatsapp')} />
            </Tabs>

            <TextField
                fullWidth
                label={credentialType === 'email' ? t('enter_email_address') : t('enter_whatsapp_number')}
                type={credentialType === 'email' ? 'email' : 'tel'}
                value={credentialValue}
                onChange={(e) => setCredentialValue(e.target.value)}
                placeholder={credentialType === 'email' ? 'your@email.com' : '+1234567890'}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            {credentialType === 'email' ? <EmailIcon /> : <WhatsAppIcon />}
                        </InputAdornment>
                    ),
                }}
                sx={{ mb: 2 }}
            />

            <Button
                fullWidth
                variant="contained"
                onClick={handleInitiateLink}
                disabled={loading || !credentialValue}
            >
                {loading ? <CircularProgress size={24} /> : t('continue')}
            </Button>

            <Button
                fullWidth
                variant="text"
                onClick={() => {
                    setStep('list');
                    setCredentialValue('');
                    setError('');
                }}
                sx={{ mt: 1 }}
            >
                {t('cancel_btn')}
            </Button>
        </Box>
    );

    // Render merge confirmation
    const renderMergeConfirm = () => (
        <Box>
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                {t('merge_warning')}
            </Alert>

            <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                    {t('account_to_merge')}
                </Typography>
                <Typography variant="h6">
                    {mergeTarget?.display_name || 'Account'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {mergeTarget?.trip_count || 0} {t('trips')}
                </Typography>
            </Box>

            <Typography variant="body2" sx={{ mb: 2 }}>
                {t('merge_explanation')}
            </Typography>

            <Button
                fullWidth
                variant="contained"
                color="warning"
                startIcon={<MergeIcon />}
                onClick={() => sendVerificationPIN('merge', mergeTarget?.pu_id)}
                disabled={loading}
                sx={{ mb: 1 }}
            >
                {loading ? <CircularProgress size={24} /> : t('confirm_merge')}
            </Button>

            <Button
                fullWidth
                variant="text"
                onClick={() => {
                    setStep('add');
                    setMergeTarget(null);
                    setError('');
                }}
            >
                {t('cancel_btn')}
            </Button>
        </Box>
    );

    // Render verification step
    const renderVerification = () => (
        <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
                {credentialType === 'email' 
                    ? t('verification_code_sent_email')
                    : t('verification_code_sent_whatsapp')
                }
            </Alert>

            {expiresIn > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                    {t('code_expires_in', { seconds: expiresIn })}
                </Typography>
            )}

            <TextField
                fullWidth
                label={t('enter_verification_code')}
                value={verificationCode}
                onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setVerificationCode(val);
                }}
                placeholder="000000"
                inputProps={{
                    maxLength: 6,
                    style: {
                        textAlign: 'center',
                        fontSize: '24px',
                        letterSpacing: '8px',
                        fontWeight: 'bold'
                    }
                }}
                sx={{ mb: 2 }}
            />

            <Button
                fullWidth
                variant="contained"
                onClick={handleVerifyPIN}
                disabled={loading || verificationCode.length !== 6}
            >
                {loading ? <CircularProgress size={24} /> : t('verify_code')}
            </Button>

            <Button
                fullWidth
                variant="text"
                onClick={() => {
                    setStep('add');
                    setVerificationCode('');
                    setVerificationId(null);
                    setError('');
                }}
                sx={{ mt: 1 }}
            >
                {t('back_to_login')}
            </Button>
        </Box>
    );

    // Render create username/password form (for WhatsApp users)
    const renderSetCredentials = () => (
        <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
                {t('create_username_password_desc')}
            </Alert>

            <TextField
                fullWidth
                label={t('username_label')}
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                sx={{ mb: 2 }}
            />

            <TextField
                fullWidth
                label={t('password_label')}
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                helperText={t('password_helper')}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                                {showNewPassword ? '🙈' : '👁'}
                            </IconButton>
                        </InputAdornment>
                    )
                }}
                sx={{ mb: 2 }}
            />

            <Button
                fullWidth
                variant="contained"
                onClick={handleSetUsernamePassword}
                disabled={loading || !newUsername || !newPassword}
            >
                {loading ? <CircularProgress size={24} /> : t('save_btn')}
            </Button>

            <Button
                fullWidth
                variant="text"
                onClick={() => { setStep('list'); setNewUsername(''); setNewPassword(''); setError(''); }}
                sx={{ mt: 1 }}
            >
                {t('cancel_btn')}
            </Button>
        </Box>
    );

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {step === 'list' && t('manage_credentials')}
                {step === 'add_email' && 'Add Email Address'}
                {step === 'add' && t('add_credential')}
                {step === 'merge_confirm' && t('merge_accounts')}
                {step === 'verify' && t('verify_code')}
                {step === 'set_credentials' && t('create_username_password')}
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                {loading && step === 'list' ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {step === 'list' && renderCredentialsList()}
                        {step === 'add_email' && renderAddEmailForm()}
                        {step === 'add' && renderAddCredential()}
                        {step === 'merge_confirm' && renderMergeConfirm()}
                        {step === 'verify' && renderVerification()}
                        {step === 'set_credentials' && renderSetCredentials()}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>{t('close')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default AccountLinking;
